import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFirestore, collection, query, getDocs, where, orderBy } from 'firebase/firestore';
import { EyeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const AdminCompletedOrdersPage = () => {
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompletedJobs = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        // Get jobs that are marked as shipped or manually completed
        const q = query(
          collection(db, 'printJobs'),
          where('paymentStatus', 'in', ['SHIPPED', 'DELIVERED']),
          orderBy('shippedAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const jobs = querySnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          shippedAtFormatted: doc.data().shippedAt?.toDate().toLocaleDateString() || 'Unknown',
          createdAtFormatted: doc.data().createdAt?.toDate().toLocaleDateString() || 'Unknown'
        }));
        
        setCompletedJobs(jobs);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching completed jobs:', error);
        setLoading(false);
      }
    };
    
    fetchCompletedJobs();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Completed Orders</h1>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-green-100 text-green-800 text-sm">
          {completedJobs.length} {completedJobs.length === 1 ? 'Order' : 'Orders'} Completed
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : completedJobs.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No completed orders found.
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Print Job ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Book ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shipped Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tracking
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Amount
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {completedJobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {job.printJobId || job.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {job.bookId || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      job.paymentStatus === 'SHIPPED' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-indigo-100 text-indigo-800'
                    }`}>
                      {job.paymentStatus?.replace(/_/g, ' ') || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {job.shippedAtFormatted}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {job.trackingNumber ? (
                      job.trackingUrl ? (
                        <a 
                          href={job.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          {job.trackingNumber}
                        </a>
                      ) : (
                        job.trackingNumber
                      )
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {job.totalAmount 
                      ? `$${parseFloat(job.totalAmount / 100).toFixed(2)}` 
                      : job.amount 
                        ? `$${parseFloat(job.amount / 100).toFixed(2)}`
                        : 'N/A'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link
                      to={`/admin/print-jobs/${job.id}`}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <EyeIcon className="mr-1.5 h-4 w-4" />
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCompletedOrdersPage;
